import type { FunctionComponent } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { FeedbackButtons } from './FeedbackButtons'
import { WebsterIcon } from './WebsterIcon'

interface DelayedFeedbackBannerProps {
  /** Called when user provides feedback */
  onRate: (rating: boolean) => void
  /** Duration to show banner in milliseconds */
  duration?: number
}

/**
 * Collapsed banner that appears for a few seconds after user closes chat
 * Prompts for feedback if conversation was closed without rating
 */
export const DelayedFeedbackBanner: FunctionComponent<DelayedFeedbackBannerProps> = ({
  onRate,
  duration = 8000, // 8 seconds default
}) => {
  const [isVisible, setIsVisible] = useState(true)
  const [hasRated, setHasRated] = useState(false)

  useEffect(() => {
    // Auto-hide after duration
    const timer = setTimeout(() => {
      setIsVisible(false)
    }, duration)

    return () => clearTimeout(timer)
  }, [duration])

  const handleRate = (rating: boolean) => {
    setHasRated(true)
    onRate(rating)
    
    // Hide after a short delay to show "thanks" message
    setTimeout(() => {
      setIsVisible(false)
    }, 2000)
  }

  const handleDismiss = () => {
    setIsVisible(false)
  }

  if (!isVisible) return null

  return (
    <div
      className="fixed bottom-6 right-[7.5rem] bg-white rounded-lg shadow-xl border border-gray-200
                 p-3 z-50 max-w-sm animate-slide-up"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-slate-900 dark:bg-slate-700
                      flex items-center justify-center overflow-visible">
          <WebsterIcon size={38} state="aware" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {!hasRated ? (
            <>
              <p className="text-sm font-medium text-gray-900 mb-2">
                How was your Webster experience?
              </p>
              <FeedbackButtons onRate={handleRate} size="sm" />
            </>
          ) : (
            <p className="text-sm text-gray-700">
              Thanks for your feedback! This helps us improve.
            </p>
          )}
        </div>

        {/* Dismiss button */}
        {!hasRated && (
          <button
            type="button"
            onClick={handleDismiss}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            title="Dismiss"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}
